import { apiPost } from "../fetch";
import { getGlobalHeadersAsObject } from "../global-headers";
import type { PersonEducation } from "../person/education";
import type { PersonExperienceType } from "../person/experiences";
import type { Person } from "../person/person";
import type { SearchableLanguage } from "../person/searchable-languages";
import type { PersonSkill } from "../person/skills";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ExternalReviewer {
	id: number;
	name: string;
	email: string;
	created: string;
}

export interface ExternalReviewSummary {
	id: number;
	token: string;
	reviewer_name: string;
	reviewer_email: string;
	expires_at: string;
	is_active: boolean;
	is_deleted: boolean;
	is_expired: boolean;
	person_count: number;
	vote_count: number;
	created: string;
}

export interface ExternalReviewVote {
	id: number;
	person_id: number;
	grade: "up" | "down";
	comment: string | null;
	created: string;
}

export interface ExternalReviewPersonSummary {
	id: number;
	name: string;
	headline: string;
	internal_vote: string | null;
}

export interface ExternalReviewDetail {
	id: number;
	token: string;
	reviewer_name: string;
	reviewer_email: string;
	expires_at: string;
	is_active: boolean;
	is_deleted: boolean;
	is_expired: boolean;
	persons: ExternalReviewPersonSummary[];
	votes: ExternalReviewVote[];
	created: string;
}

export interface PublicPersonVote {
	grade: "up" | "down";
	comment: string | null;
	created: string;
}

export interface PublicPerson extends Person {
	experience: PersonExperienceType[];
	education: PersonEducation[];
	languages: SearchableLanguage[];
	skills: PersonSkill[];
	vote: PublicPersonVote | null;
}

export interface PublicReviewData {
	reviewer_name: string;
	assignment_name: string;
	persons: PublicPerson[];
	total_count: number;
	voted_count: number;
}

// ---------------------------------------------------------------------------
// Authenticated endpoints — ExternalReviewers
// ---------------------------------------------------------------------------

export async function listExternalReviewers(): Promise<ExternalReviewer[]> {
	const response = await fetch("/api/v2/external-reviewers/", {
		headers: getGlobalHeadersAsObject(),
	});
	const data = await response.json();
	return data.results;
}

export async function createExternalReviewer(
	name: string,
	email: string
): Promise<ExternalReviewer> {
	const response = await fetch("/api/v2/external-reviewers/", {
		method: "POST",
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify({ name, email }),
	});
	return response.json();
}

// ---------------------------------------------------------------------------
// Authenticated endpoints — ExternalReviews (nested under assignment)
// ---------------------------------------------------------------------------

export async function listExternalReviews(
	assignmentId: number
): Promise<ExternalReviewSummary[]> {
	const response = await fetch(
		`/api/v2/assignment/${assignmentId}/external-reviews/`,
		{ headers: getGlobalHeadersAsObject() }
	);
	const data = await response.json();
	return data.results;
}

export async function getExternalReview(
	assignmentId: number,
	reviewId: number
): Promise<ExternalReviewDetail> {
	const response = await fetch(
		`/api/v2/assignment/${assignmentId}/external-reviews/${reviewId}/`,
		{ headers: getGlobalHeadersAsObject() }
	);
	return response.json();
}

export async function createExternalReview(
	assignmentId: number,
	reviewerId: number,
	personIds: number[],
	message: string,
	expiresInDays?: number
): Promise<ExternalReviewSummary> {
	const body: Record<string, unknown> = {
		reviewer_id: reviewerId,
		person_ids: personIds,
		message,
	};
	if (expiresInDays !== undefined) {
		body.expires_in_days = expiresInDays;
	}
	const response = await fetch(
		`/api/v2/assignment/${assignmentId}/external-reviews/`,
		{
			method: "POST",
			headers: {
				...getGlobalHeadersAsObject(),
				"Content-Type": "application/json",
			},
			body: JSON.stringify(body),
		}
	);
	return response.json();
}

/** AI draft of the recruiter's note to the reviewer about these candidates. */
export async function draftExternalReviewMessage(
	assignmentId: number,
	personIds: number[],
	reviewerName: string,
	regenerate: boolean
): Promise<string> {
	const { message } = (await apiPost(
		`/api/v2/assignment/${assignmentId}/external-reviews/draft_message/`,
		{ person_ids: personIds, reviewer_name: reviewerName, regenerate }
	)) as { message: string };
	return message;
}

export async function revokeExternalReview(
	assignmentId: number,
	reviewId: number
): Promise<void> {
	await fetch(`/api/v2/assignment/${assignmentId}/external-reviews/${reviewId}/`, {
		method: "DELETE",
		headers: getGlobalHeadersAsObject(),
	});
}

/** Delete a revoked review (soft-delete — sets is_deleted=true on backend). */
export async function deleteExternalReview(
	assignmentId: number,
	reviewId: number
): Promise<void> {
	await fetch(`/api/v2/assignment/${assignmentId}/external-reviews/${reviewId}/`, {
		method: "DELETE",
		headers: getGlobalHeadersAsObject(),
	});
}

// ---------------------------------------------------------------------------
// Public endpoints — no auth required
// ---------------------------------------------------------------------------

export async function getPublicReview(token: string): Promise<PublicReviewData> {
	const response = await fetch(`/api/v2/external-review/${token}/`);
	if (response.status === 410) {
		throw new Error("REVIEW_EXPIRED");
	}
	return response.json();
}

export async function submitPublicVote(
	token: string,
	personId: number,
	grade: "up" | "down",
	comment?: string
): Promise<ExternalReviewVote> {
	const response = await fetch(`/api/v2/external-review/${token}/vote/`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({
			person_id: personId,
			grade,
			comment: comment || null,
		}),
	});
	if (response.status === 410) {
		throw new Error("REVIEW_EXPIRED");
	}
	return response.json();
}
